import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Beaker, FlaskConical, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const Petrochemical = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/petrochemical"]?.title} description={seoData["industries/petrochemical"]?.description} path="/industries/petrochemical" keywords={seoData["industries/petrochemical"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Petrochemical"
        description="Coating and corrosion repair contractor for petrochemical plants. Chemical-resistant coatings, immersion grade tank linings, and pipeline repair for the most corrosive processing environments."
        backgroundKey="petrochemical"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Aggressive chemical attack on steel and concrete substrates',
              'High-temperature corrosion in process vessels and reactors',
              'Acid and caustic tank lining failures',
              'Pipe-in-pipe and CUI (corrosion under insulation) deterioration',
              'Secondary containment degradation at tank farms',
              'Fugitive emissions from corroded flanges and fittings'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-purple-50 rounded-lg">
                <Beaker className="text-purple-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-purple-600 mr-3 flex-shrink-0" size={22} />
                  Chemical-Resistant Tank Linings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Immersion-grade epoxy and vinyl ester lining systems for acid tanks, caustic tanks, solvent storage, and process vessels. Chemical resistance testing and compatibility verification for your specific process fluids.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-purple-600 mr-3 flex-shrink-0" size={22} />
                  High-Temperature Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Ceramic-filled and high-temperature coating systems for reactors, heat exchangers, columns, and exhaust systems operating up to 400°F. Thermal barrier coatings for energy efficiency and corrosion protection.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-purple-600 mr-3 flex-shrink-0" size={22} />
                  Containment & Spill Prevention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Chemical containment coatings for dike areas, trenches, and secondary containment. Bund lining contractor services meeting EPA and local regulatory requirements for petrochemical storage facilities.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-purple-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <FlaskConical className="text-purple-600 mr-3 flex-shrink-0" size={22} />
                  Pipeline & Piping Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Emergency leak sealing and long-term corrosion protection for process piping, utility lines, and cooling water systems. Composite wraps and epoxy systems applied without hot work in classified areas.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Petrochemical?</h2>
              <div className="space-y-3">
                {['Chemical resistance — coatings tested and verified for your specific process fluids',
                  'Classified area experience — TWIC credentialed, trained for work in hazardous locations',
                  'Zero hot work — cold-applied systems eliminate ignition risk',
                  'In-service repairs — many solutions applied while equipment remains operational',
                  'Full QA/QC documentation including DFT readings, adhesion testing, and holiday detection',
                  'Manufacturer-backed warranties with extended coverage options'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-purple-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Chemical Processing Experts</h3>
              <p className="text-purple-100 mb-6">BCS understands the unique corrosion challenges of petrochemical environments. Our team is trained in chemical compatibility, application in classified areas, and regulatory compliance.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/protective-coatings')}>
                View Protective Coatings <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Petrochemical Assets</h2>
          <p className="text-blue-100 mb-8">Contact our petrochemical specialists for a corrosion assessment and coating recommendation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Petrochemical;
